import { useCallback, useEffect, useState } from "react";

// Hook
export const useLocalStorage = (key: string, initialValue?: string) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Return current value of localStorage item if initialValue is not defined
      const newValue = initialValue ? initialValue : item;
      // Parse stored json or if none return initialValue
      return item ? item : newValue;
    } catch (error) {
      // If error also return initialValue
      return initialValue ?? null;
    }
  });

  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage.
  const setValue = useCallback(
    (value: string, dispatchEvent = false) => {
      try {
        // Save state
        setStoredValue(value);
        // Save to local storage
        window.localStorage.setItem(key, value);
        if (dispatchEvent) {
          window.dispatchEvent(new Event("storage"));
        }
      } catch (error) {
        if (error instanceof DOMException && error.name === "QuotaExceededError") {
          window.localStorage.removeItem(key);
          setStoredValue(null);
        } else {
          console.error(error);
        }
        throw new Error("Erreur avec l'enregistrement local, veuillez contacter un administrateur.");
      }
    },
    [key]
  );

  const removeValue = useCallback(() => {
    window.localStorage.removeItem(key);
    window.dispatchEvent(new Event("storage"));
  }, [key]);

  return [storedValue, setValue, removeValue] as const;
};

export const useLocalStorageWatcher = (key: string) => {
  const [val, setVal] = useState(window.localStorage.getItem(key));

  useEffect(() => {
    const handler = () => {
      return val !== window.localStorage.getItem(key) && setVal(window.localStorage.getItem(key));
    };
    window.addEventListener("storage", handler);
    () => window.removeEventListener("storage", handler);
  }, [val]);

  useEffect(() => {
    setVal(window.localStorage.getItem(key));
  }, [key]);

  return [val, setVal] as const;
};
